import { useCallback, useMemo } from "react";
import { useLandingPagesQuery } from "../api/queries/index";
import { useTogglePublishedMutation } from "../api/mutations/index";
import { searchValues } from "../lib/options";
import type {
  LandingPage,
  PaginationState,
  QueryParams,
  SearchValues,
} from "../types";
import { useDelete, useUrlSearch } from "@/shared/hooks";

interface UseLandingPagesPageReturn {
  data: LandingPage[];
  isLoading: boolean;
  error: Error | null;
  totalItems: number;
  search: { show: boolean; values: SearchValues };
  updateSearch: (params: { show?: boolean; values?: SearchValues }) => void;
  handleSearchSubmit: () => void;
  handleSearchReset: () => void;
  pg: PaginationState;
  updatePg: (newPg: Partial<PaginationState>) => void;
  refetch: () => void;
  deleteHandler: (id: number) => Promise<void>;
  togglePublish: (id: number, value: boolean) => Promise<void>;
}

export const useLandingPagesPage = (): UseLandingPagesPageReturn => {
  const {
    appliedValues,
    draftValues,
    updateDraft,
    applySearch,
    resetSearch,
    page,
    size,
    setPage,
    setSize,
    showFilters,
    setShowFilters,
  } = useUrlSearch<SearchValues>({ defaultValues: searchValues });

  const queryParams: QueryParams = useMemo(
    () => ({ page, perSize: size, search: appliedValues }),
    [page, size, appliedValues],
  );

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useLandingPagesQuery(queryParams);

  const data = queryData?.items || [];
  const totalItems = queryData?.totalCount || 0;

  const togglePublishMutation = useTogglePublishedMutation();

  const updatePg = useCallback(
    (newPg: Partial<PaginationState>) => {
      if (newPg.page !== undefined) setPage(newPg.page);
      if (newPg.size !== undefined) setSize(newPg.size);
    },
    [setPage, setSize],
  );

  const updateSearch = useCallback(
    (updates: { show?: boolean; values?: SearchValues }) => {
      if (updates.show !== undefined) setShowFilters(updates.show);
      if (updates.values) {
        Object.entries(updates.values).forEach(([key, value]) => {
          updateDraft(key as keyof SearchValues, value);
        });
      }
    },
    [setShowFilters, updateDraft],
  );

  const { deleteHandler } = useDelete(data, refetch, "landing-pages");

  const togglePublish = useCallback(
    async (id: number, value: boolean) => {
      await togglePublishMutation.mutateAsync({ id, value });
    },
    [togglePublishMutation],
  );

  return {
    data,
    isLoading,
    error: error as Error | null,
    totalItems,
    search: { show: showFilters, values: draftValues },
    updateSearch,
    handleSearchSubmit: applySearch,
    handleSearchReset: resetSearch,
    pg: { page, size },
    updatePg,
    refetch,
    deleteHandler,
    togglePublish,
  };
};
